<script setup lang="ts">
import VideoThumbnailIcon from './VideoThumbnailIcon.vue';

defineProps<{
  resourceIds: string[];
  name: string;
  description: string;
  disabled: boolean;
  isStarting: boolean;
}>();

const emit = defineEmits<{
  (e: 'start'): void;
}>();
</script>

<template>
  <div class="quizter-card-wrapper position-relative">
    <button
      type="button"
      class="quizter-card d-flex align-center ga-4 text-left"
      :disabled="disabled || isStarting"
      :aria-busy="isStarting"
      data-testid="my-quizter-item"
      @click="emit('start')"
    >
      <VideoThumbnailIcon class="quizter-card__icon" :resource-ids="resourceIds" :size="100" />
      <div class="d-flex align-center justify-space-between flex-grow-1 ga-4 overflow-hidden pe-12">
        <div class="d-flex flex-column ga-2 overflow-hidden">
          <span class="chalkduster-font text-h6 text-truncate">
            {{ name }}
          </span>
          <span class="chalkboard-font text-body-2 opacity-70 description-clamp">
            {{ description }}
          </span>
        </div>
      </div>
    </button>

    <div v-if="isStarting" class="quizter-starting-overlay d-flex align-center">
      <div class="quizter-starting-overlay__spinner d-flex align-center justify-center">
        <v-progress-circular indeterminate size="32"></v-progress-circular>
      </div>
    </div>

    <slot name="actions" />
  </div>
</template>

<style scoped lang="scss">
// Shared solid gray for the card border and the icon outline so they match exactly
$card-outline: rgb(var(--v-theme-outline-variant));
$card-outline-hover: rgb(var(--v-theme-outline));

.description-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.quizter-card-wrapper {
  width: 100%;
}

.quizter-starting-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 12px;
  background-color: rgba(var(--v-theme-scrim), 0.4);

  &__spinner {
    // Matches the thumbnail's width (VideoThumbnailIcon size="100") so the spinner centers over it.
    width: 100px;
  }
}

.quizter-card {
  width: 100%;
  padding: 0;
  border: 2px solid $card-outline;
  border-radius: 12px;
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  cursor: pointer;
  transition: all 0.2s;

  .quizter-card__icon {
    border-radius: 12px;
    box-shadow: 0 0 0 2px $card-outline;
  }

  &:hover:not(:disabled) {
    transform: scale(1.02);
    background-color: rgba(var(--v-theme-on-surface), 0.12);
    border-color: $card-outline-hover;

    .quizter-card__icon {
      box-shadow: 0 0 0 2px $card-outline-hover;
    }
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}
</style>
